import React, { useEffect, useState, useRef } from 'react';
import { claimAudioChannel, releaseAudioChannel } from '../utils/audioChannel';

export default function BlackmailAnimation({ targetCountry, onComplete, gameId }) {
  const [videoSequence, setVideoSequence] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const videoRef = useRef(null);
  const audioRefs = useRef({ barSounds: null, music: null, targetBlackmailSound: null });

  useEffect(() => {
    // 🔈 Claim audio channel so blackmail SFX play over iOS
    claimAudioChannel();
    // All 21 blackmail videos
    const allVideos = [
      '/assets/blackmail/blackmail-video-1.mp4',
      '/assets/blackmail/blackmail-video-2.mp4',
      '/assets/blackmail/blackmail-video-3.mp4',
      '/assets/blackmail/blackmail-video-4-couple-camera.mp4',
      '/assets/blackmail/blackmail-video-5-room-papers.mp4',
      '/assets/blackmail/blackmail-video-6-cars-cameras.mp4',
      '/assets/blackmail/blackmail-video-7-balcony-couple.mp4',
      '/assets/blackmail/blackmail-18.mp4',
      'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/45eq599j_Blackmail%20Assassin%20Rooftop.mp4', // Assassin rooftop - better lighting
      '/assets/videos/blackmail-cabaret.mp4',
      '/assets/videos/blackmail-couch.mp4',
      '/assets/videos/blackmail-theatre.mp4',
      '/assets/videos/blackmail-surveillance-van.mp4',
      '/assets/videos/blackmail-catwalk.mp4',
      '/assets/videos/blackmail-tumbling.mp4',
      '/assets/videos/blackmail-inside-apt.mp4',
      '/assets/videos/blackmail-scuffle-apt.mp4',
      '/assets/videos/blackmail-money.mp4',
      '/assets/blackmail/blackmail-warehouse-karaoke.mp4',
      '/assets/videos/blackmail-bar-alley.mp4',
      '/assets/videos/blackmail-car-water.mp4'
    ];
    
    // Get or create persistent playlist for this game
    // Playlist state keys (non-sensitive: just video order tracking for UX continuity)
    const playlistKey = `blackmail_playlist_${gameId || 'default'}`;
    const indexKey = `blackmail_index_${gameId || 'default'}`;
    
    let playlist = null;
    let currentIndex = 0;
    
    try {
      // Note: localStorage used here is safe - only stores video playlist order, no credentials
      const storedPlaylist = localStorage.getItem(playlistKey);
      const storedIndex = localStorage.getItem(indexKey);
      
      if (storedPlaylist) {
        playlist = JSON.parse(storedPlaylist);
        currentIndex = parseInt(storedIndex || '0', 10);
        
        // If we've reached the end of the playlist, reshuffle
        if (currentIndex >= playlist.length) {
          console.log('🔄 Playlist complete! Reshuffling all 21 videos...');
          playlist = [...allVideos].sort(() => Math.random() - 0.5);
          currentIndex = 0;
          localStorage.setItem(playlistKey, JSON.stringify(playlist));
          localStorage.setItem(indexKey, '0');
        }
      } else {
        // First time - create new shuffled playlist
        console.log('🎬 Creating new blackmail playlist (21 videos shuffled)');
        playlist = [...allVideos].sort(() => Math.random() - 0.5);
        localStorage.setItem(playlistKey, JSON.stringify(playlist));
        localStorage.setItem(indexKey, '0');
      }
    } catch (error) {
      console.error('Error loading playlist from localStorage:', error);
      // Fallback to random selection
      playlist = [...allVideos].sort(() => Math.random() - 0.5);
      currentIndex = 0;
    }
    
    // Get next 2 videos from playlist
    const selectedVideos = [
      playlist[currentIndex],
      playlist[currentIndex + 1] || playlist[0] // Wrap around if at end
    ];
    
    setVideoSequence(selectedVideos);
    
    // Update index for next blackmail action
    const newIndex = currentIndex + 2;
    localStorage.setItem(indexKey, newIndex.toString());
    
    console.log(`🎬 Blackmail videos (${currentIndex + 1}-${currentIndex + 2} of ${playlist.length}): ${selectedVideos[0].split('/').pop()} → ${selectedVideos[1].split('/').pop()}`);
    console.log(`📊 Progress: ${newIndex}/${playlist.length} videos seen in current cycle`);

    // AUDIO MIXING: Reduce background sounds to 50%, boost target-specific to 100% (max)
    // This creates a 2:1 ratio emphasizing the blackmail reaction sounds
    
    // Play bar sounds at REDUCED volume (50%)
    const barSounds = new Audio('/assets/audio/blackmail-effect.mp3');
    barSounds.volume = 0.5;  // Reduced from 0.7 to 0.5 (50%)
    barSounds.play().catch(e => console.log('Bar sounds play failed:', e));
    audioRefs.current.barSounds = barSounds;

    // Play random spy music at REDUCED volume (50%)
    const spyTracks = [
      '/assets/audio/blackmail-music-1.mp3',
      '/assets/audio/blackmail-music-2.mp3',
      '/assets/audio/blackmail-music-3.mp3',
      '/assets/audio/blackmail-music-4.mp3'
    ];
    const selectedTrack = spyTracks[Math.floor(Math.random() * spyTracks.length)];
    const music = new Audio(selectedTrack);
    music.volume = 0.5;  // Reduced from 0.6 to 0.5 (50%)
    music.play().catch(e => console.log('Spy music play failed:', e));
    audioRefs.current.music = music;

    // If USA, EU, RUSSIA, or NORTH KOREA is the target, play country-specific blackmail reaction sound at MAXIMUM volume
    // This is the STAR of the audio - play at 100% (browser max) to emphasize it
    let targetBlackmailSound = null;
    
    if (targetCountry === 'USA') {
      const usaBlackmailSounds = [
        '/assets/audio/usa-blackmail-1-ep-files.mp3',
        '/assets/audio/usa-blackmail-2-moved.mp3',
        '/assets/audio/usa-blackmail-3-psy.mp3'
      ];
      const randomUsaSound = usaBlackmailSounds[Math.floor(Math.random() * usaBlackmailSounds.length)];
      targetBlackmailSound = new Audio(randomUsaSound);
      targetBlackmailSound.volume = 1.0;  // BOOSTED to 100% (max) - was 0.7
      targetBlackmailSound.currentTime = 1.0; // Skip first 1 second (silence at beginning)
      targetBlackmailSound.play().catch(e => console.log('USA blackmail sound play failed:', e));
      console.log(`🎵 Playing USA-specific blackmail sound at 100% volume: ${randomUsaSound}`);
    } else if (targetCountry === 'EU') {
      const euBlackmailSounds = [
        '/assets/audio/eu-blackmail-1-uk.mp3',
        '/assets/audio/eu-blackmail-2-prince-andrew-ep2.mp3',
        '/assets/audio/eu-blackmail-3-prince-andrew-ep.mp3',
        '/assets/audio/eu-blackmail-4-paperclips.mp3',
        '/assets/audio/eu-blackmail-5-banana.mp3'
      ];
      const randomEuSound = euBlackmailSounds[Math.floor(Math.random() * euBlackmailSounds.length)];
      targetBlackmailSound = new Audio(randomEuSound);
      targetBlackmailSound.volume = 1.0;  // BOOSTED to 100% (max) - was 0.7
      targetBlackmailSound.currentTime = 1.0; // Skip first 1 second (silence at beginning)
      targetBlackmailSound.play().catch(e => console.log('EU blackmail sound play failed:', e));
      console.log(`🎵 Playing EU-specific blackmail sound at 100% volume: ${randomEuSound}`);
    } else if (targetCountry === 'RUS') {
      const russiaBlackmailSounds = [
        '/assets/audio/russia-blackmail-1-statue.mp3',
        '/assets/audio/russia-blackmail-2-radio.mp3',
        '/assets/audio/russia-blackmail-3-frozen-lake.mp3'
      ];
      const randomRussiaSound = russiaBlackmailSounds[Math.floor(Math.random() * russiaBlackmailSounds.length)];
      targetBlackmailSound = new Audio(randomRussiaSound);
      targetBlackmailSound.volume = 1.0;  // 100% volume for maximum impact
      targetBlackmailSound.currentTime = 1.0; // Skip first 1 second (silence at beginning)
      targetBlackmailSound.play().catch(e => console.log('Russia blackmail sound play failed:', e));
      console.log(`🎵 Playing RUSSIA-specific blackmail sound at 100% volume: ${randomRussiaSound}`);
    } else if (targetCountry === 'NK') {
      const nkBlackmailSounds = [
        '/assets/audio/nk-blackmail-1-karaoke.mp3',
        '/assets/audio/nk-blackmail-2-broccoli.mp3'
      ];
      const randomNkSound = nkBlackmailSounds[Math.floor(Math.random() * nkBlackmailSounds.length)];
      targetBlackmailSound = new Audio(randomNkSound);
      targetBlackmailSound.volume = 0.5;  // 50% volume (was too loud)
      targetBlackmailSound.currentTime = 1.0; // Skip first 1 second (silence at beginning)
      targetBlackmailSound.play().catch(e => console.log('NK blackmail sound play failed:', e));
      console.log(`🎵 Playing NORTH KOREA-specific blackmail sound at 50% volume: ${randomNkSound}`);
    } else if (targetCountry === 'CHN') {
      const chinaBlackmailSounds = [
        '/assets/audio/china-blackmail-1-pandas.mp3',
        '/assets/audio/china-blackmail-2-gridlock.mp3'
      ];
      const randomChinaSound = chinaBlackmailSounds[Math.floor(Math.random() * chinaBlackmailSounds.length)];
      targetBlackmailSound = new Audio(randomChinaSound);
      targetBlackmailSound.volume = 0.5;  // 50% volume (was too loud)
      targetBlackmailSound.currentTime = 1.0; // Skip first 1 second (silence at beginning)
      targetBlackmailSound.play().catch(e => console.log('China blackmail sound play failed:', e));
      console.log(`🎵 Playing CHINA-specific blackmail sound at 50% volume: ${randomChinaSound}`);
    }
    
    if (targetBlackmailSound) {
      audioRefs.current.targetBlackmailSound = targetBlackmailSound;
    }

    // Animation duration: 10 seconds (2 videos × ~5 seconds each)
    const timer = setTimeout(() => {
      onComplete();
    }, 10000);

    return () => {
      clearTimeout(timer);
      // Clean up all audio
      if (audioRefs.current.barSounds) {
        audioRefs.current.barSounds.pause();
        audioRefs.current.barSounds.currentTime = 0;
      }
      if (audioRefs.current.music) {
        audioRefs.current.music.pause();
        audioRefs.current.music.currentTime = 0;
      }
      if (audioRefs.current.targetBlackmailSound) {
        audioRefs.current.targetBlackmailSound.pause();
        audioRefs.current.targetBlackmailSound.currentTime = 0;
      }
      // 🔈 Release audio channel so background music resumes
      releaseAudioChannel();
    };
  }, [targetCountry, onComplete]);

  // Handle video/image end - play next in sequence
  const handleVideoEnd = () => {
    if (currentVideoIndex < videoSequence.length - 1) {
      console.log(`🎬 Blackmail media ${currentVideoIndex + 1} ended, playing next...`);
      setCurrentVideoIndex(currentVideoIndex + 1);
    }
  };

  // Auto-play video when it loads or changes (or show image for 3 seconds)
  useEffect(() => {
    const currentMedia = videoSequence[currentVideoIndex];
    if (!currentMedia) return;

    // Check if it's an image (PNG, JPG, etc.)
    const isImage = currentMedia.match(/\.(png|jpg|jpeg|gif|webp)$/i);

    if (isImage) {
      // For images, show for 3 seconds then advance
      console.log('🖼️ Showing blackmail image for 3 seconds...');
      const imageTimer = setTimeout(() => {
        handleVideoEnd();
      }, 3000);
      return () => clearTimeout(imageTimer);
    } else if (videoRef.current) {
      // For videos, auto-play
      videoRef.current.play().catch(e => console.log('Video autoplay failed:', e));
    }
  }, [videoSequence, currentVideoIndex]);

  // Check if current media is an image
  const currentMedia = videoSequence[currentVideoIndex];
  const isImage = currentMedia?.match(/\.(png|jpg|jpeg|gif|webp)$/i);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 animate-fadeIn">
      {/* Warning text */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-red-900/90 border-4 border-red-500 px-8 py-4 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="text-red-500 font-mono text-2xl uppercase tracking-wider font-bold animate-pulse">
              🕵️ SECRET MEETING 🕵️
            </div>
          </div>
        </div>
      </div>

      {/* Blackmail media scene - video or image */}
      <div className="relative w-full h-full flex items-center justify-center">
        {isImage ? (
          /* Show image for assassin/photo scenes */
          <img
            src={currentMedia}
            alt="Blackmail Evidence"
            className="max-w-4xl max-h-[80vh] object-contain rounded-lg shadow-2xl animate-fadeIn"
            style={{
              filter: 'brightness(0.9) contrast(1.1)',
              animation: 'fadeIn 1s ease-in-out'
            }}
          />
        ) : (
          /* Show video for motion scenes */
          <video
            ref={videoRef}
            src={currentMedia}
            autoPlay
            muted
            playsInline
            onEnded={handleVideoEnd}
            className="max-w-4xl max-h-[80vh] object-contain rounded-lg shadow-2xl animate-fadeIn"
            style={{
              filter: 'brightness(0.9) contrast(1.1)',
              animation: 'fadeIn 1s ease-in-out'
            }}
          />
        )}
        
        {/* Vignette effect */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, transparent 40%, rgba(0, 0, 0, 0.8) 100%)'
          }}
        />
      </div>

      {/* Bottom text */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-50">
        <div className="text-gray-400 font-mono text-lg uppercase tracking-wider">
          💼 Covert Operations in Progress...
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}
